import type { ReactNode } from "react";
import { Link } from "react-router";
import type { BetaFeatures } from "@/lib/shared/beta-features";
import { useBetaFeatures } from "@/lib/shared/beta-features";

interface FeatureFlagRouteProps {
  /** Beta feature flag key. Route renders children when ON, 404 when OFF. */
  flag: keyof BetaFeatures;
  children: ReactNode;
}

/**
 * Route guard that gates a route behind a beta feature flag.
 *
 * When the flag is OFF (default), the route renders an in-layout 404 panel so
 * the URL is unreachable but the surrounding shell stays intact. When the flag
 * is ON (toggled via Settings → Beta Features), the children render normally.
 *
 * Used for the pre-beta feature freeze (sw-b7h) — see docs/feature-freeze.md.
 *
 * The 404 panel is duplicated inline (rather than importing
 * `InLayoutNotFoundPage`) so this guard does not pull NotFoundPage's chunk
 * into every route entry — keeps NotFoundPage purely lazy-loaded for the
 * outer-layout 404 fallback.
 */
export function FeatureFlagRoute({ flag, children }: FeatureFlagRouteProps) {
  const { features } = useBetaFeatures();
  if (!features[flag]) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center py-16 px-4 text-center">
        <h1 className="text-6xl font-bold text-surface-900 dark:text-white mb-4">404</h1>
        <p className="text-xl text-surface-600 dark:text-surface-400 mb-2">
          We can&apos;t find that page
        </p>
        <p className="text-sm text-surface-500 dark:text-surface-400 mb-8">
          It may have been moved or removed.
        </p>
        <Link
          to="/home"
          className="px-4 py-2 rounded-md bg-brand-500 text-white hover:bg-brand-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }
  return <>{children}</>;
}
